import { useState } from "react"
import Button from "components/ui/Button"
import Modal from "components/ui/Modal"
import EditableField from "components/ui/EditableField"
import moment from "moment"
import { useUpdateBuyerCampaignMutation, useUpdateBuyerCampaignMessageMutation } from "apis/buyerCampaign"
import { showToast } from "components/ui/toast"

const MESSAGE_STATUS_BADGES = {
  scheduled: "bg-purple-100 text-purple-800",
  pending: "bg-yellow-100 text-yellow-800",
  approved: "bg-blue-100 text-blue-800",
  sent: "bg-green-100 text-green-800",
  rejected: "bg-red-100 text-red-800",
  skipped: "bg-lightgray text-darkergray",
}

const CAMPAIGN_STATUS_BADGES = {
  pending: "bg-yellow-100 text-yellow-800",
  active: "bg-green-100 text-green-800",
  completed: "bg-lightgray text-darkergray",
}

const CAMPAIGN_STATUSES = ["pending", "active", "completed"]

const getAvailableActions = (status) => {
  switch (status) {
    case "scheduled":
      return [
        { action: "approved", label: "Approve", className: "bg-blue-600 text-white hover:bg-blue-700" },
        { action: "skipped", label: "Skip", className: "bg-lightgray text-darkergray hover:bg-lightgray" },
      ]
    case "pending":
      return [
        { action: "approved", label: "Approve", className: "bg-blue-600 text-white hover:bg-blue-700" },
        { action: "rejected", label: "Reject", className: "bg-red-100 text-red-700 hover:bg-red-200" },
        { action: "skipped", label: "Skip", className: "bg-lightgray text-darkergray hover:bg-lightgray" },
      ]
    case "approved":
      return [
        { action: "rejected", label: "Reject", className: "bg-red-100 text-red-700 hover:bg-red-200" },
        { action: "skipped", label: "Skip", className: "bg-lightgray text-darkergray hover:bg-lightgray" },
      ]
    case "rejected":
      return [
        { action: "approved", label: "Re-approve", className: "bg-blue-600 text-white hover:bg-blue-700" },
      ]
    case "skipped":
      return [
        { action: "scheduled", label: "Re-schedule", className: "bg-purple-100 text-purple-700 hover:bg-purple-200" },
      ]
    default:
      return []
  }
}

const BuyerCampaignDetail = ({ campaign, onBack, templates = [] }) => {
  const [expandedMessage, setExpandedMessage] = useState(null)
  const [localCampaign, setLocalCampaign] = useState(campaign)

  // Campaign status modal
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false)
  const [pendingCampaignStatus, setPendingCampaignStatus] = useState(null)

  // Message action modal
  const [isMessageActionModalOpen, setIsMessageActionModalOpen] = useState(false)
  const [pendingMessageAction, setPendingMessageAction] = useState(null)

  // Schedule date edit modal
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false)
  const [pendingScheduleEdit, setPendingScheduleEdit] = useState(null) // { index, value }

  // Add template modal
  const [isAddTemplateModalOpen, setIsAddTemplateModalOpen] = useState(false)
  const [addTemplateForm, setAddTemplateForm] = useState({ templateId: "", scheduled: "", message: "" })

  const { mutate: updateCampaign, isPending: isUpdating } = useUpdateBuyerCampaignMutation()
  const { mutate: updateMessageContent } = useUpdateBuyerCampaignMessageMutation()

  const toggleMessage = (index) => {
    setExpandedMessage(expandedMessage === index ? null : index)
  }

  // ── Campaign status ────────────────────────────────────────────────────────
  const handleCampaignStatusChange = (newStatus) => {
    setPendingCampaignStatus(newStatus)
    setIsStatusModalOpen(true)
  }

  const confirmCampaignStatusChange = () => {
    updateCampaign(
      { id: localCampaign._id, status: pendingCampaignStatus },
      {
        onSuccess: () => {
          setLocalCampaign((prev) => ({ ...prev, status: pendingCampaignStatus }))
          showToast("success", `Campaign status changed to ${pendingCampaignStatus}`)
        },
      }
    )
    setIsStatusModalOpen(false)
    setPendingCampaignStatus(null)
  }

  // ── Message status action ──────────────────────────────────────────────────
  const handleMessageAction = (messageIndex, action) => {
    setPendingMessageAction({ index: messageIndex, action })
    setIsMessageActionModalOpen(true)
  }

  const confirmMessageAction = () => {
    const { index, action } = pendingMessageAction
    const updatedMessages = localCampaign.messages.map((msg, i) => {
      if (i !== index) return msg
      const now = new Date().toISOString()
      return {
        ...msg,
        status: action,
        ...(action === "approved" ? { approvedAt: now } : {}),
        ...(action === "rejected" ? { rejectedAt: now } : {}),
      }
    })
    updateCampaign(
      { id: localCampaign._id, messages: updatedMessages },
      {
        onSuccess: () => {
          setLocalCampaign((prev) => ({ ...prev, messages: updatedMessages }))
          showToast("success", `Message ${action} successfully`)
        },
      }
    )
    setIsMessageActionModalOpen(false)
    setPendingMessageAction(null)
  }

  // ── Schedule date edit ─────────────────────────────────────────────────────
  const handleScheduleEdit = (messageIndex) => {
    const current = localCampaign.messages[messageIndex]?.scheduled
    setPendingScheduleEdit({
      index: messageIndex,
      value: current ? moment(current).format("YYYY-MM-DDTHH:mm") : "",
    })
    setIsScheduleModalOpen(true)
  }

  const confirmScheduleEdit = () => {
    const { index, value } = pendingScheduleEdit
    if (!value) {
      showToast("error", "Please enter a valid date and time")
      return
    }
    const updatedMessages = localCampaign.messages.map((msg, i) => {
      if (i !== index) return msg
      return { ...msg, scheduled: new Date(value).toISOString() }
    })
    updateCampaign(
      { id: localCampaign._id, messages: updatedMessages },
      {
        onSuccess: () => {
          setLocalCampaign((prev) => ({ ...prev, messages: updatedMessages }))
          showToast("success", "Schedule updated successfully")
        },
      }
    )
    setIsScheduleModalOpen(false)
    setPendingScheduleEdit(null)
  }

  // ── Add template to campaign ───────────────────────────────────────────────
  const handleAddTemplate = () => {
    setAddTemplateForm({ templateId: "", scheduled: "", message: "" })
    setIsAddTemplateModalOpen(true)
  }

  const confirmAddTemplate = () => {
    if (!addTemplateForm.templateId) {
      showToast("error", "Please select a template")
      return
    }
    const selectedTemplate = templates.find((t) => t._id === addTemplateForm.templateId)
    const newMessage = {
      templateId: addTemplateForm.templateId,
      status: "scheduled",
      scheduled: addTemplateForm.scheduled ? new Date(addTemplateForm.scheduled).toISOString() : null,
      isSent: false,
      isAgentNotified: false,
      message: addTemplateForm.message,
    }
    const updatedMessages = [...localCampaign.messages, newMessage]
    updateCampaign(
      { id: localCampaign._id, messages: updatedMessages },
      {
        onSuccess: () => {
          // Attach the full template object locally so name shows immediately
          const newMessageWithTemplate = { ...newMessage, templateId: selectedTemplate }
          setLocalCampaign((prev) => ({
            ...prev,
            messages: [...prev.messages, newMessageWithTemplate],
          }))
          showToast("success", `Template "${selectedTemplate?.name}" added to campaign`)
        },
      }
    )
    setIsAddTemplateModalOpen(false)
    setAddTemplateForm({ templateId: "", scheduled: "", message: "" })
  }

  if (!localCampaign) return null

  return (
    <div>
      {/* ── Campaign Status Modal ─────────────────────────────────────────── */}
      <Modal
        isOpen={isStatusModalOpen}
        onClose={() => { setIsStatusModalOpen(false); setPendingCampaignStatus(null) }}
        title="Change Campaign Status"
      >
        <p className="text-sm text-darkgray mb-4">
          Are you sure you want to change the campaign status from{" "}
          <span className="font-semibold capitalize">{localCampaign.status}</span> to{" "}
          <span className="font-semibold capitalize">{pendingCampaignStatus}</span>?
        </p>
        <div className="flex justify-end space-x-3">
          <Button onClick={() => { setIsStatusModalOpen(false); setPendingCampaignStatus(null) }} className="outline-button">Cancel</Button>
          <Button onClick={confirmCampaignStatusChange} className="black-button" disabled={isUpdating}>
            {isUpdating ? "Updating..." : "Confirm"}
          </Button>
        </div>
      </Modal>

      {/* ── Message Action Modal ──────────────────────────────────────────── */}
      <Modal
        isOpen={isMessageActionModalOpen}
        onClose={() => { setIsMessageActionModalOpen(false); setPendingMessageAction(null) }}
        title="Confirm Message Action"
      >
        {pendingMessageAction && (
          <>
            <p className="text-sm text-darkgray mb-4">
              Are you sure you want to{" "}
              <span className="font-semibold capitalize">{pendingMessageAction.action}</span>{" "}
              message #{pendingMessageAction.index + 1} (
              {localCampaign.messages[pendingMessageAction.index]?.templateId?.name || "Untitled"})?
            </p>
            <div className="flex justify-end space-x-3">
              <Button onClick={() => { setIsMessageActionModalOpen(false); setPendingMessageAction(null) }} className="outline-button">Cancel</Button>
              <Button onClick={confirmMessageAction} className="black-button" disabled={isUpdating}>
                {isUpdating ? "Updating..." : "Confirm"}
              </Button>
            </div>
          </>
        )}
      </Modal>

      {/* ── Schedule Edit Modal ───────────────────────────────────────────── */}
      <Modal
        isOpen={isScheduleModalOpen}
        onClose={() => { setIsScheduleModalOpen(false); setPendingScheduleEdit(null) }}
        title="Edit Scheduled Date & Time"
      >
        {pendingScheduleEdit && (
          <>
            <p className="text-sm text-darkgray mb-3">
              Update the scheduled send date and time for message #{pendingScheduleEdit.index + 1}.
            </p>
            <input
              type="datetime-local"
              value={pendingScheduleEdit.value}
              onChange={(e) => setPendingScheduleEdit((prev) => ({ ...prev, value: e.target.value }))}
              className="form-input w-full mb-4 border border-darkgray"
            />
            <div className="flex justify-end space-x-3">
              <Button onClick={() => { setIsScheduleModalOpen(false); setPendingScheduleEdit(null) }} className="outline-button">Cancel</Button>
              <Button onClick={confirmScheduleEdit} className="black-button" disabled={isUpdating}>
                {isUpdating ? "Saving..." : "Save"}
              </Button>
            </div>
          </>
        )}
      </Modal>

      {/* ── Add Template Modal ────────────────────────────────────────────── */}
      <Modal
        isOpen={isAddTemplateModalOpen}
        onClose={() => { setIsAddTemplateModalOpen(false); setAddTemplateForm({ templateId: "", scheduled: "", message: "" }) }}
        title="Add Template to Campaign"
      >
        <div className="space-y-4">
          <div>
            <label className="form-label">Select Template <span className="text-red-500">*</span></label>
            <select
              value={addTemplateForm.templateId}
              onChange={(e) => setAddTemplateForm((prev) => ({ ...prev, templateId: e.target.value, message: templates.find((t) => t._id === e.target.value)?.content || "" }))}
              className="form-select border border-darkgray"
            >
              <option value="">— Choose a template —</option>
              {templates.map((t) => (
                <option key={t._id} value={t._id}>
                  {t.name} ({t.trigger} · {t.action}{t.offsetDays ? ` ${t.offsetDays}d` : ""})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label">Scheduled Date & Time <span className="text-darkgray text-xs">(optional)</span></label>
            <input
              type="datetime-local"
              value={addTemplateForm.scheduled}
              onChange={(e) => setAddTemplateForm((prev) => ({ ...prev, scheduled: e.target.value }))}
              className="form-input w-full border border-darkgray"
            />
          </div>
          {addTemplateForm.templateId && (() => {
            const t = templates.find((x) => x._id === addTemplateForm.templateId)
            if (!t) return null
            return (
              <div className="bg-lightergray rounded p-3 text-sm space-y-1">
                <p><span className="font-medium text-darkgray">Action:</span> {t.action}</p>
                <p><span className="font-medium text-darkgray">Trigger:</span> {t.trigger}</p>
                {t.saleProcess?.length > 0 && <p><span className="font-medium text-darkgray">Sale Process:</span> {t.saleProcess.join(", ")}</p>}
              </div>
            )
          })()}
        </div>
        <div className="flex justify-end space-x-3 mt-5">
          <Button onClick={() => { setIsAddTemplateModalOpen(false); setAddTemplateForm({ templateId: "", scheduled: "", message: "" }) }} className="outline-button">Cancel</Button>
          <Button onClick={confirmAddTemplate} className="black-button" disabled={isUpdating}>
            {isUpdating ? "Adding..." : "Add to Campaign"}
          </Button>
        </div>
      </Modal>

      {/* ── Header ───────────────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h3 className="text-lg font-semibold">
            Campaign for {localCampaign.propertyId?.address || "Unknown Property"}
          </h3>
          <p className="text-darkgray">
            Owner: {localCampaign.userId?.name || "Unknown"} ({localCampaign.userId?.email || ""})
          </p>
        </div>
        <Button onClick={onBack} className="black-button">Back</Button>
      </div>

      {/* ── Disclaimer ───────────────────────────────────────────────────── */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-6 flex gap-3">
        <svg className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
        </svg>
        <div>
          <p className="text-sm font-semibold text-yellow-800">Admin Controls — Limited Editing</p>
          <p className="text-sm text-yellow-700 mt-1">
            You can change the <strong>campaign status</strong>, update <strong>message statuses</strong> (approve / reject / skip), edit <strong>scheduled dates</strong>, and <strong>edit message content</strong>. Edited message content is flagged as <strong>overridden</strong> — the AI will use your version as its base at send time instead of the master template content.
          </p>
        </div>
      </div>

      {/* ── Campaign Info Cards ───────────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white p-4 rounded-lg shadow-sm">
          <h4 className="font-medium text-black mb-3">Campaign Details</h4>
          <div className="space-y-2">
            <div>
              <span className="text-sm font-medium text-darkgray">Property:</span>
              <p className="text-black">{localCampaign.propertyId?.address || "-"}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-darkgray">Type:</span>
              <p className="text-black capitalize">
                {localCampaign.type === "privateTreaty" ? "Private Treaty" : localCampaign.type || "-"}
              </p>
            </div>
            {localCampaign.campaignWeeks && (
              <div>
                <span className="text-sm font-medium text-darkgray">Campaign Weeks:</span>
                <p className="text-black">{localCampaign.campaignWeeks} weeks</p>
              </div>
            )}
            <div>
              <span className="text-sm font-medium text-darkgray">Status:</span>
              <div className="flex items-center gap-2 mt-1">
                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${CAMPAIGN_STATUS_BADGES[localCampaign.status] || "bg-lightgray text-darkergray"}`}>
                  {localCampaign.status?.charAt(0).toUpperCase() + localCampaign.status?.slice(1)}
                </span>
              </div>
              <div className="flex gap-1 mt-2 flex-wrap">
                {CAMPAIGN_STATUSES.filter((s) => s !== localCampaign.status).map((s) => (
                  <button
                    key={s}
                    onClick={() => handleCampaignStatusChange(s)}
                    disabled={isUpdating}
                    className="px-2 py-1 text-xs rounded border border-lightgray hover:bg-lightergray capitalize disabled:opacity-50"
                  >
                    Set {s}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm">
          <h4 className="font-medium text-black mb-3">Dates</h4>
          <div className="space-y-2">
            <div>
              <span className="text-sm font-medium text-darkgray">Closing Date:</span>
              <p className="text-black">
                {localCampaign.closingDate ? moment(localCampaign.closingDate).format("MMMM D, YYYY h:mm A") : "-"}
              </p>
            </div>
            <div>
              <span className="text-sm font-medium text-darkgray">Created:</span>
              <p className="text-black">
                {localCampaign.createdAt ? moment(localCampaign.createdAt).format("MMMM D, YYYY") : "-"}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm">
          <h4 className="font-medium text-black mb-3">Recipients & Info</h4>
          <div className="space-y-2">
            <div>
              <span className="text-sm font-medium text-darkgray">Recipients:</span>
              <p className="text-black">{localCampaign.recipients?.length || 0} buyers</p>
            </div>
            {localCampaign.locationInfo?.schools && (
              <div>
                <span className="text-sm font-medium text-darkgray">Schools:</span>
                <p className="text-black text-sm truncate">{localCampaign.locationInfo.schools}</p>
              </div>
            )}
            {localCampaign.locationInfo?.transport && (
              <div>
                <span className="text-sm font-medium text-darkgray">Transport:</span>
                <p className="text-black text-sm truncate">{localCampaign.locationInfo.transport}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Messages ─────────────────────────────────────────────────────── */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <h4 className="text-lg font-medium">Messages ({localCampaign.messages?.length || 0})</h4>
          {templates.length > 0 && (
            <button
              onClick={handleAddTemplate}
              disabled={isUpdating}
              className="px-3 py-1.5 text-xs font-medium rounded bg-black text-white hover:bg-gray-800 disabled:opacity-50"
            >
              + Add Template
            </button>
          )}
        </div>

        {!localCampaign.messages || localCampaign.messages.length === 0 ? (
          <div className="text-center py-12 bg-lightergray rounded-md">
            <p className="text-darkgray">No messages in this campaign</p>
          </div>
        ) : (
          <div className="space-y-4">
            {localCampaign.messages.map((msg, index) => {
              const actions = getAvailableActions(msg.status)
              return (
                <div key={msg._id || index} className="border border-lightgray rounded-lg shadow-sm overflow-hidden">
                  {/* Row header */}
                  <div
                    className="bg-lightergray px-4 py-3 border-b flex justify-between items-center cursor-pointer"
                    onClick={() => toggleMessage(index)}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium text-darkgray">#{index + 1}</span>
                      <div>
                        <h5 className="font-medium text-black">
                          {msg.templateId?.name || `Message ${index + 1}`}
                        </h5>
                        <p className="text-xs text-darkgray">
                          {msg.scheduled
                            ? `Scheduled: ${moment(msg.scheduled).format("MMM D, YYYY h:mm A")}`
                            : "Not scheduled"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full ${MESSAGE_STATUS_BADGES[msg.status] || "bg-lightgray text-darkergray"}`}>
                        {msg.status?.charAt(0).toUpperCase() + msg.status?.slice(1)}
                      </span>
                      <svg className={`w-4 h-4 text-darkgray transition-transform ${expandedMessage === index ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>

                  {/* Expanded body */}
                  {expandedMessage === index && (
                    <div className="p-4 bg-white space-y-4">
                      {/* Message content — editable via EditableField */}
                      <div className="w-full">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium text-mediumgray">Message Content</span>
                          {msg.isOverridden && (
                            <span className="text-xs text-orange-600 italic">⚠ Manually overridden</span>
                          )}
                        </div>
                        {msg.status !== "sent" ? (
                          <EditableField
                            value={msg.message || ""}
                            onSave={(newContent) => {
                              if (!msg._id) {
                                showToast("error", "Cannot edit unsaved message — save the campaign first or refresh the page")
                                return
                              }
                              updateMessageContent(
                                { id: localCampaign._id, messageId: msg._id, messageContent: newContent },
                                {
                                  onSuccess: () => {
                                    setLocalCampaign((prev) => ({
                                      ...prev,
                                      messages: prev.messages.map((m, i) =>
                                        i === index ? { ...m, message: newContent, isOverridden: true } : m
                                      ),
                                    }))
                                    showToast("success", "Message content updated")
                                  },
                                }
                              )
                            }}
                            // className="w-full flex"
                          />
                        ) : (
                          <div className="mt-1 p-3 bg-lightergray rounded text-sm text-darkgray whitespace-pre-wrap">
                            {msg.message || <span className="italic text-mediumgray">No content yet — will be generated at send time.</span>}
                          </div>
                        )}
                      </div>

                      {/* Meta grid */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        <div>
                          <span className="font-medium text-darkgray">Status</span>
                          <p className="capitalize">{msg.status}</p>
                        </div>
                        <div>
                          <span className="font-medium text-darkgray">Sent</span>
                          <p>{msg.isSent ? "Yes" : "No"}</p>
                        </div>
                        {msg.sentAt && (
                          <div>
                            <span className="font-medium text-darkgray">Sent At</span>
                            <p>{moment(msg.sentAt).format("MMM D, YYYY h:mm A")}</p>
                          </div>
                        )}
                        {msg.approvedAt && (
                          <div>
                            <span className="font-medium text-darkgray">Approved At</span>
                            <p>{moment(msg.approvedAt).format("MMM D, YYYY h:mm A")}</p>
                          </div>
                        )}
                        {msg.rejectedAt && (
                          <div>
                            <span className="font-medium text-darkgray">Rejected At</span>
                            <p>{moment(msg.rejectedAt).format("MMM D, YYYY h:mm A")}</p>
                          </div>
                        )}
                        <div>
                          <span className="font-medium text-darkgray">Agent Notified</span>
                          <p>{msg.isAgentNotified ? "Yes" : "No"}</p>
                        </div>
                      </div>

                      {/* Template info badges */}
                      {msg.templateId && typeof msg.templateId === "object" && (
                        <div className="pt-2 border-t">
                          <span className="text-sm font-medium text-darkgray">Template Info</span>
                          <div className="flex gap-2 mt-1">
                            <span className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-800">{msg.templateId.trigger}</span>
                            <span className="px-2 py-0.5 text-xs rounded-full bg-lightgray text-darkergray">{msg.templateId.action}</span>
                          </div>
                        </div>
                      )}

                      {/* Admin actions */}
                      {msg.status !== "sent" && (
                        <div className="pt-3 border-t space-y-3">
                          {/* Schedule edit */}
                          <div>
                            <span className="text-sm font-medium text-darkgray block mb-1">Scheduled Date & Time</span>
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-darkgray">
                                {msg.scheduled ? moment(msg.scheduled).format("MMM D, YYYY h:mm A") : "Not set"}
                              </span>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleScheduleEdit(index) }}
                                disabled={isUpdating}
                                className="px-2 py-1 text-xs rounded border border-lightgray hover:bg-lightergray disabled:opacity-50"
                              >
                                Edit
                              </button>
                            </div>
                          </div>

                          {/* Status action buttons */}
                          {actions.length > 0 && (
                            <div>
                              <span className="text-sm font-medium text-darkgray block mb-1">Change Status</span>
                              <div className="flex flex-wrap gap-2">
                                {actions.map(({ action, label, className }) => (
                                  <button
                                    key={action}
                                    onClick={(e) => { e.stopPropagation(); handleMessageAction(index, action) }}
                                    disabled={isUpdating}
                                    className={`px-3 py-1.5 text-xs font-medium rounded ${className} disabled:opacity-50`}
                                  >
                                    {label}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* ── Location Info ─────────────────────────────────────────────────── */}
      {localCampaign.locationInfo && (
        <div className="mb-6">
          <h4 className="text-lg font-medium mb-4">Location Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {localCampaign.locationInfo.schools && (
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <span className="text-sm font-medium text-darkgray">Schools</span>
                <p className="text-black text-sm mt-1">{localCampaign.locationInfo.schools}</p>
              </div>
            )}
            {localCampaign.locationInfo.shopsAndCafes && (
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <span className="text-sm font-medium text-darkgray">Shops & Cafes</span>
                <p className="text-black text-sm mt-1">{localCampaign.locationInfo.shopsAndCafes}</p>
              </div>
            )}
            {localCampaign.locationInfo.transport && (
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <span className="text-sm font-medium text-darkgray">Transport</span>
                <p className="text-black text-sm mt-1">{localCampaign.locationInfo.transport}</p>
              </div>
            )}
            {localCampaign.locationInfo.lifestyle && (
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <span className="text-sm font-medium text-darkgray">Lifestyle</span>
                <p className="text-black text-sm mt-1">{localCampaign.locationInfo.lifestyle}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default BuyerCampaignDetail
